import axios from 'axios';
import notificationStore from '../services/notificationStore';
import { getActiveProjectId } from '../constants/projectConstants';

/**
 * Raise a notification in the header bell for a content/schedule change.
 * Tagged with the city (projectId) so the city-scoped feed shows it only under
 * that city — defaults to the active city when the caller didn't pass one.
 * Rapid repeats (e.g. a per-slot create loop) are collapsed by the store's
 * dedupe window, so callers can fire freely. Best-effort — never throws.
 */
const notifyFeed = ({ screenId, projectId, title, message, type = 'schedule-updated', severity = 'info' } = {}) => {
  try {
    notificationStore.push({
      type,
      severity,
      title: title || 'Schedule updated',
      message: message || (screenId ? `Schedule changed for ${screenId}.` : 'A schedule was updated.'),
      deviceId: screenId || null,
      projectId: projectId || getActiveProjectId(),
    });
  } catch {
    /* notifications are non-critical */
  }
};

/**
 * Backend base URL for CMS content APIs.
 * Kept configurable through env for non-production environments.
 */
const DEFAULT_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
export const CMS_API_BASE_URL =
  import.meta.env.VITE_CMS_API_BASE_URL || `${DEFAULT_API_BASE_URL}/api/sumadhura-cms`;

// Backend's POST /upload-policy returns the AWS-SDK-v3-presigner shape:
//   { uploadUrl, fields: { bucket, key, Policy, X-Amz-Algorithm, ... },
//     expiresAt, contentId, filePath, fileName, bucket, cdnBaseUrl, cdnUrl, projectId, section }
// The SigV4 fields live inside `fields`; `contentId`/`filePath` stay top-level.
const REQUIRED_POLICY_FIELDS = ['uploadUrl', 'fields', 'contentId', 'filePath'];
const REQUIRED_FIELD_KEYS = [
  'key',
  'Policy',
  'X-Amz-Algorithm',
  'X-Amz-Credential',
  'X-Amz-Date',
  'X-Amz-Signature',
];

/**
 * Extracts a readable message from Axios/JS errors so UI can show actionable feedback.
 * @param {unknown} error
 * @param {string} fallback
 * @returns {string}
 */
const getErrorMessage = (error, fallback) => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    // If `message` looks like an error code (SCREAMING_SNAKE_CASE) and there's
    // a human-readable `detail` field, prefer the detail. Backend errors like
    // "ASPECT_RATIO_MISMATCH" + detail "Content X is portrait but screen is
    // landscape" should show the prose, not the code.
    const messageStr = typeof data?.message === 'string' ? data.message : '';
    const detailStr = typeof data?.detail === 'string' ? data.detail : '';
    const looksLikeErrorCode = /^[A-Z][A-Z0-9_]*$/.test(messageStr.trim());
    if (looksLikeErrorCode && detailStr) {
      return detailStr;
    }
    const msg =
      messageStr ||
      detailStr ||
      (typeof data?.error === 'string' && data.error) ||
      error.message;
    return msg || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

/**
 * Validates upload policy structure before attempting the S3 form upload.
 * @param {Object} policy
 */
const assertValidPolicy = (policy) => {
  const missingTop = REQUIRED_POLICY_FIELDS.filter((field) => !policy?.[field]);
  if (missingTop.length > 0) {
    throw new Error(`Upload policy missing required fields: ${missingTop.join(', ')}`);
  }
  const missingFieldKeys = REQUIRED_FIELD_KEYS.filter((k) => !policy.fields?.[k]);
  if (missingFieldKeys.length > 0) {
    throw new Error(`Upload policy.fields missing: ${missingFieldKeys.join(', ')}`);
  }
};

/**
 * Step 1: Request a signed upload policy from backend.
 * @param {string} fileName
 * @param {string} projectId
 * @param {string} section
 * @returns {Promise<Object>}
 */
export const getUploadPolicy = async (fileName, projectId, section) => {
  try {
    const response = await axios.post(`${CMS_API_BASE_URL}/upload-policy`, {
      fileName,
      projectId,
      section,
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to get upload policy'));
  }
};

/**
 * Step 2: Upload file directly to S3 using the presigned POST policy.
 *
 * Backend uses @aws-sdk/s3-presigned-post which returns `{ uploadUrl, fields }`.
 * We append every field from `fields` (order matters for S3 — file must be last)
 * then POST to `uploadUrl` (which is the region-aware virtual-hosted URL
 * generated by the SDK, not the legacy global s3.amazonaws.com endpoint).
 *
 * @param {File} file
 * @param {Object} policy
 * @returns {Promise<void>}
 */
export const uploadFileToS3 = async (file, policy) => {
  assertValidPolicy(policy);

  const formData = new FormData();
  for (const [name, value] of Object.entries(policy.fields)) {
    formData.append(name, String(value));
  }
  formData.append('file', file); // Must always be the last field.

  const response = await fetch(policy.uploadUrl, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`S3 upload failed (${response.status}): ${errorBody || 'Unknown S3 error'}`);
  }
};

/**
 * Step 3: Save uploaded content metadata in backend database.
 * @param {Object} contentData
 * @returns {Promise<Object>}
 */
export const createContent = async (contentData) => {
  try {
    const response = await axios.post(`${CMS_API_BASE_URL}/content`, contentData);
    const kind =
      contentData?.type === 'video' ? 'video'
      : contentData?.type === 'image' ? 'image'
      : contentData?.section === 'website' ? 'web page'
      : 'content';
    const name = contentData?.title || contentData?.fileName || '';
    notifyFeed({
      type: 'content-added',
      severity: 'success',
      title: 'New content added',
      message: `A new ${kind}${name ? ` "${name}"` : ''} was added to the library.`,
      projectId: contentData?.projectId,
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to save content metadata'));
  }
};

/**
 * Fetch all content entries for a given project section.
 * Endpoint: GET /sumadhura-cms/:projectId/:section
 * @param {string} projectId
 * @param {string} section
 * @returns {Promise<Array>}
 */
export const listSectionContent = async (projectId, section) => {
  try {
    const response = await axios.get(`${CMS_API_BASE_URL}/${projectId}/${section}`);
    return Array.isArray(response.data?.items) ? response.data.items : [];
  } catch (error) {
    throw new Error(getErrorMessage(error, `Failed to fetch content for section: ${section}`));
  }
};

/**
 * Fetch screen-level schedule for a given project and TV/screen.
 * Endpoint: GET /sumadhura-cms/screen-schedule?projectId=...&screenId=...
 * @param {string} projectId
 * @param {string} screenId
 * @returns {Promise<Array<{contentId:string,startTime:string,endTime:string}>>}
 */
export const getScreenSchedule = async (projectId, screenId) => {
  try {
    const response = await axios.get(`${CMS_API_BASE_URL}/screen-schedule`, {
      params: { projectId, screenId },
    });
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.schedule)) return data.schedule;
    return [];
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch screen schedule'));
  }
};

/**
 * Full GET /screen-schedule response (`schedule[]` and per-slot loop fields; no rotation — use rotation APIs).
 */
export const getScreenScheduleDocument = async (projectId, screenId) => {
  try {
    const response = await axios.get(`${CMS_API_BASE_URL}/screen-schedule`, {
      params: { projectId, screenId },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch screen schedule'));
  }
};

/**
 * Save (upsert) embedded screen schedule (one doc per screen).
 * POST /screen-schedule — body: `projectId`, `screenId`, `schedule[]` only.
 * Rotation is not accepted here; use `putScreenRotation` / device rotation endpoints.
 * @param {string} projectId
 * @param {string} screenId
 * @param {Array} schedule
 * @returns {Promise<Object>}
 */
export const upsertScreenSchedule = async (projectId, screenId, schedule) => {
  try {
    const response = await axios.post(`${CMS_API_BASE_URL}/screen-schedule`, {
      projectId,
      screenId,
      schedule,
    });

    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to save screen schedule'));
  }
};

/**
 * Fetch project-level manifest describing all sections and preload priorities.
 * Endpoint: GET /sumadhura-cms/manifest/:projectId
 * @param {string} projectId
 * @returns {Promise<Object>}
 */
/**
 * @param {string} projectId
 * @param {string|null} [screenId] Optional — returns screenRotationDegrees, scheduleSlots, etc. when set
 */
export const getProjectManifest = async (projectId, screenId = null) => {
  try {
    const params = {};
    if (screenId && String(screenId).trim()) params.screenId = String(screenId).trim();
    const response = await axios.get(`${CMS_API_BASE_URL}/manifest/${projectId}`, {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch project manifest'));
  }
};

/**
 * Fetch the "now playing" map for every screen in a project.
 * Endpoint: GET /sumadhura-cms/now-playing?projectId=X
 *
 * Returns an object shaped like:
 * {
 *   success: true,
 *   serverTime: "2026-04-10T15:37:00.000Z",
 *   nowPlaying: {
 *     [screenId]: {
 *       contentId, title, thumbnailUrl, mediaType,
 *       slotScheduleId, slotStartTime, slotEndTime,
 *       isLoop, loopIndex, loopTotal, mergeOrigin,
 *     }
 *   }
 * }
 *
 * Used by the dashboard TVList to show what each screen is currently playing
 * without making one API call per row.
 */
export const getNowPlayingForProject = async (projectId) => {
  try {
    const response = await axios.get(`${CMS_API_BASE_URL}/now-playing`, {
      params: { projectId },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch now-playing state'));
  }
};

/**
 * Fetch content-centric dashboard status for all content in a project.
 * Endpoint: GET /sumadhura-cms/content-status?projectId=X
 *
 * Returns: { success, serverTime, summary: { total, playing, scheduled, idle, neverScheduled },
 *            content: Array<{ contentId, name, title, section, mediaType, thumbnailUrl,
 *                             status, playingOnScreens, upcoming, lastPlayed, uploadedAt, validUntil }> }
 */
export const getContentStatus = async (projectId) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    const response = await axios.get(`${CMS_API_BASE_URL}/content-status`, {
      params: { projectId },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch content status'));
  }
};

/**
 * Create a schedule entry for a specific screen.
 * Endpoint: POST /sumadhura-cms/schedules
 *
 * Payload by type:
 * - Single day: { projectId, screenId, contentId, date, startTime, endTime }
 *   → date "2026-02-18" = content plays only on that day between startTime and endTime.
 * - Date range: { projectId, screenId, contentId, startDate, endDate, startTime, endTime }
 *   → content plays daily from startDate to endDate between startTime and endTime each day.
 *
 * @param {{projectId:string,screenId:string,contentId:string,startTime:string,endTime:string,date?:string,startDate?:string,endDate?:string}} payload
 * @returns {Promise<Object>}
 */
export const createCmsSchedule = async (payload) => {
  try {
    const response = await axios.post(`${CMS_API_BASE_URL}/schedules`, payload);
    notifyFeed({
      screenId: payload?.screenId,
      projectId: payload?.projectId,
      message: payload?.screenId
        ? `A new slot was scheduled on ${payload.screenId}.`
        : 'A new slot was scheduled.',
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create schedule'));
  }
};

/**
 * Create multiple schedule slots for one screen in one request.
 * Endpoint: POST `/schedules/bulk`
 *
 * Each **`slots[]`** element supports the **same fields** as `POST /schedules` (see scheduler loop doc §4.3), including optional **`loopEnabled`** and **`loopItems`** (`contentId`, `durationSeconds`, `mediaType`). The server may also accept snake_case mirrors (`loop_enabled`, `loop_items`, `content_id`, …) like the single-slot route.
 *
 * Example — one normal slot + one looping slot:
 * ```json
 * {
 *   "projectId": "sumadhura_edition",
 *   "screenId": "TV_MAIN",
 *   "date": "2026-03-30",
 *   "slots": [
 *     { "contentId": "Morning", "startTime": "09:00", "endTime": "12:00" },
 *     {
 *       "contentId": "LoopRoot",
 *       "startTime": "12:00",
 *       "endTime": "18:00",
 *       "loopEnabled": true,
 *       "loopItems": [
 *         { "contentId": "SlideA", "durationSeconds": 10, "mediaType": "image" },
 *         { "contentId": "VidB", "durationSeconds": 30, "mediaType": "video" }
 *       ]
 *     }
 *   ]
 * }
 * ```
 *
 * Example — **one** bulk slot for a long window (e.g. 2h); player cycles `loopItems` until `endTime` (same asset repeated = multiple objects, same `contentId`):
 * ```json
 * {
 *   "projectId": "sumadhura_edition",
 *   "screenId": "TV_MAIN",
 *   "date": "2026-03-30",
 *   "slots": [
 *     {
 *       "contentId": "loop_main",
 *       "startTime": "10:00",
 *       "endTime": "12:00",
 *       "loopEnabled": true,
 *       "loopItems": [
 *         { "contentId": "v1", "durationSeconds": 60, "mediaType": "video" },
 *         { "contentId": "img1", "durationSeconds": 15, "mediaType": "image" }
 *       ]
 *     }
 *   ]
 * }
 * ```
 *
 * @param {{ projectId: string, screenId: string, date?: string, startDate?: string, endDate?: string, slots: Array<Record<string, unknown>> }} payload
 * @returns {Promise<{ success?: boolean, message?: string, count?: number, data?: unknown[] }>}
 */
export const createBulkCmsSchedule = async (payload) => {
  try {
    const response = await axios.post(`${CMS_API_BASE_URL}/schedules/bulk`, payload);
    const count = Array.isArray(payload?.slots) ? payload.slots.length : null;
    notifyFeed({
      screenId: payload?.screenId,
      projectId: payload?.projectId,
      message: payload?.screenId
        ? `${count ? `${count} slots` : 'Slots'} scheduled on ${payload.screenId}.`
        : 'Multiple slots were scheduled.',
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create bulk schedule'));
  }
};

/**
 * Fetch schedules filtered by project, optionally filtered by screen/device ID.
 * Endpoint: GET /sumadhura-cms/schedules?projectId=...&screenId=...
 * Response: { success: true, data: Array<Schedule> }
 * @param {{projectId:string,screenId?:string|null,includeCancelled?:boolean,scheduleId?:string|null}} input
 * @returns {Promise<{ success: boolean, data: Array }>}
 */
export const getCmsSchedules = async (input = {}) => {
  try {
    const projectId = input?.projectId;
    const screenId = input?.screenId?.trim?.() || null;
    const scheduleId = input?.scheduleId?.trim?.() || null;
    const includeCancelled = input?.includeCancelled === true;

    if (!projectId) {
      throw new Error('projectId is required to fetch schedules');
    }

    const params = { projectId };
    if (screenId) params.screenId = screenId;
    if (scheduleId) params.scheduleId = scheduleId;
    if (includeCancelled) params.includeCancelled = 'true';
    const response = await axios.get(`${CMS_API_BASE_URL}/schedules`, { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch schedules'));
  }
};

/**
 * PATCH /schedules/:scheduleId/cancel — soft-cancel slot (hidden from default GET).
 */
export const cancelCmsSchedule = async (scheduleId) => {
  try {
    const id = String(scheduleId || '').trim();
    if (!id) throw new Error('scheduleId is required');
    const response = await axios.patch(`${CMS_API_BASE_URL}/schedules/${encodeURIComponent(id)}/cancel`);
    notifyFeed({
      type: 'schedule-cancelled',
      title: 'Schedule slot cancelled',
      message: 'A scheduled slot was cancelled.',
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to cancel schedule'));
  }
};

/**
 * POST /schedules/cancel-all — cancel every active slot for a screen.
 */
export const cancelAllCmsSchedulesForScreen = async ({ projectId, screenId }) => {
  try {
    if (!projectId || !String(screenId || '').trim()) {
      throw new Error('projectId and screenId are required');
    }
    const response = await axios.post(`${CMS_API_BASE_URL}/schedules/cancel-all`, {
      projectId,
      screenId: String(screenId).trim(),
    });
    notifyFeed({
      type: 'schedule-cancelled',
      screenId: String(screenId).trim(),
      projectId,
      title: 'Schedules cleared',
      message: `All scheduled slots were cancelled for ${String(screenId).trim()}.`,
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to cancel all schedules for screen'));
  }
};

/**
 * POST /schedules/:scheduleId/remove-loop-item — remove one content item from
 * a loop without cancelling the slot. Deletes slot if last item removed.
 */
export const removeLoopItemFromSlot = async (scheduleId, contentId) => {
  try {
    const response = await axios.post(
      `${CMS_API_BASE_URL}/schedules/${encodeURIComponent(scheduleId)}/remove-loop-item`,
      { contentId },
    );
    notifyFeed({
      message: 'A video was removed from a scheduled slot.',
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to remove loop item'));
  }
};

/**
 * PUT /schedules/:scheduleId — partial update (cancelled slots return 404).
 */
export const updateCmsSchedule = async (scheduleId, payload) => {
  try {
    const id = String(scheduleId || '').trim();
    if (!id) throw new Error('scheduleId is required');
    const response = await axios.put(`${CMS_API_BASE_URL}/schedules/${encodeURIComponent(id)}`, payload);
    notifyFeed({
      screenId: payload?.screenId,
      projectId: payload?.projectId,
      message: payload?.screenId
        ? `A scheduled slot was updated on ${payload.screenId}.`
        : 'A scheduled slot was updated.',
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update schedule'));
  }
};

/**
 * GET /screens/:screenId/resolution
 * Returns native resolution for the screen (from device registration).
 * @param {string} screenId
 * @returns {Promise<{success: boolean, screenId: string, nativeResolution: {width: number, height: number, aspectRatio: string}|null}>}
 */
export const getScreenResolution = async (screenId) => {
  try {
    const sid = String(screenId || '').trim();
    if (!sid) throw new Error('screenId is required');
    const response = await axios.get(`${CMS_API_BASE_URL}/screens/${encodeURIComponent(sid)}/resolution`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch screen resolution'));
  }
};

/**
 * PUT /screens/:screenId/resolution
 * Manual override for screen native resolution.
 * @param {string} screenId
 * @param {{width: number, height: number}} resolution
 * @returns {Promise<Object>}
 */
export const putScreenResolution = async (screenId, { width, height }) => {
  try {
    const sid = String(screenId || '').trim();
    if (!sid) throw new Error('screenId is required');
    const response = await axios.put(`${CMS_API_BASE_URL}/screens/${encodeURIComponent(sid)}/resolution`, {
      width,
      height,
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update screen resolution'));
  }
};

/**
 * GET /screens/:screenId/rotation?projectId=
 * Rotation is read from tvAssignment (first device for this screen / tvId), not from screen-schedule storage.
 */
export const getScreenRotation = async (projectId, screenId) => {
  try {
    const sid = String(screenId || '').trim();
    if (!projectId || !sid) throw new Error('projectId and screenId are required');
    const response = await axios.get(`${CMS_API_BASE_URL}/screens/${encodeURIComponent(sid)}/rotation`, {
      params: { projectId },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch screen rotation'));
  }
};

const ROTATION_DEGREES_ALLOWED = [0, 90, 180, 270];

/**
 * PUT /screens/:screenId/rotation — body: `{ projectId, rotationDegrees: 0|90|180|270 }`.
 * Persisted on tvAssignment; emits `sumadhura:screen:config` on the TV socket namespace.
 */
export const putScreenRotation = async (projectId, screenId, rotationDegrees) => {
  const sid = String(screenId || '').trim();
  if (!projectId || !sid) throw new Error('projectId and screenId are required');
  const deg = Number(rotationDegrees);
  if (!ROTATION_DEGREES_ALLOWED.includes(deg)) {
    throw new Error('rotationDegrees must be 0, 90, 180, or 270');
  }
  try {
    const response = await axios.put(`${CMS_API_BASE_URL}/screens/${encodeURIComponent(sid)}/rotation`, {
      projectId,
      rotationDegrees: deg,
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update screen rotation'));
  }
};

/**
 * GET /device-screen/rotation?deviceId= (required) & optional screenId & projectId
 * `projectId` is optional (logging / filtering); omitting it does not by itself force `rotationSource: 'default'`.
 * Ids are trimmed before the request so list vs Mongo `deviceId` stays consistent.
 */
export const getDeviceScreenRotation = async ({ deviceId, screenId = null, projectId = null }) => {
  const did = String(deviceId || '').trim();
  if (!did) throw new Error('deviceId is required');
  const params = { deviceId: did };
  const sid = screenId != null && String(screenId).trim() ? String(screenId).trim() : null;
  const pid = projectId != null && String(projectId).trim() ? String(projectId).trim() : null;
  if (sid) params.screenId = sid;
  if (pid) params.projectId = pid;
  try {
    const response = await axios.get(`${CMS_API_BASE_URL}/device-screen/rotation`, { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch device rotation'));
  }
};

/**
 * PUT /device-screen/rotation
 * Body: `deviceId` (trimmed), `rotationDegrees`, optional `screenId`, optional `projectId`.
 * Backend accepts angle from `rotationDegrees` | `screenRotationDegrees` | `screen_rotation_degrees`; we send all three for compatibility.
 * `screenId` is optional when `tvId` is null (effective room: body.screenId ?? assignment.tvId ?? cms.tvId).
 */
export const putDeviceScreenRotation = async ({ deviceId, screenId, rotationDegrees, projectId }) => {
  const did = String(deviceId || '').trim();
  if (!did) throw new Error('deviceId is required');
  const deg = Number(rotationDegrees);
  if (!ROTATION_DEGREES_ALLOWED.includes(deg)) {
    throw new Error('rotationDegrees must be 0, 90, 180, or 270');
  }
  const body = {
    deviceId: did,
    rotationDegrees: deg,
    screenRotationDegrees: deg,
    screen_rotation_degrees: deg,
  };
  const sid = screenId != null ? String(screenId).trim() : '';
  if (sid) body.screenId = sid;
  if (projectId != null && String(projectId).trim()) {
    body.projectId = String(projectId).trim();
  }
  try {
    const response = await axios.put(`${CMS_API_BASE_URL}/device-screen/rotation`, body);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update device rotation'));
  }
};

/**
 * GET /screens/:screenId/config?projectId=
 */
export const getScreenConfig = async (projectId, screenId) => {
  try {
    const sid = String(screenId || '').trim();
    if (!projectId || !sid) throw new Error('projectId and screenId are required');
    const response = await axios.get(`${CMS_API_BASE_URL}/screens/${encodeURIComponent(sid)}/config`, {
      params: { projectId },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch screen config'));
  }
};

/**
 * GET /screens/history — screen / TV playback history.
 */
export const getScreensHistory = async (params = {}) => {
  try {
    const response = await axios.get(`${CMS_API_BASE_URL}/screens/history`, { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch screen history'));
  }
};

/**
 * POST /screens/telemetry — playback state (typically from APK; optional dashboard use).
 */
export const postScreenTelemetry = async (payload) => {
  try {
    const response = await axios.post(`${CMS_API_BASE_URL}/screens/telemetry`, payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to post telemetry'));
  }
};

/**
 * GET /sumadhura-cms/playlists?projectId=...
 * Response: { success, data: Array<Playlist> }
 */
export const getPlaylists = async (projectId) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    const response = await axios.get(`${CMS_API_BASE_URL}/playlists`, { params: { projectId } });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch playlists'));
  }
};

/**
 * POST /sumadhura-cms/playlists
 * Body: { projectId, name, description?, contentIds }
 * Response: { success, data: Array<Playlist> }
 */
export const createPlaylist = async (payload) => {
  try {
    const response = await axios.post(`${CMS_API_BASE_URL}/playlists`, payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create playlist'));
  }
};

/**
 * PUT /sumadhura-cms/playlists/:playlistId
 * Body: { name, description?, contentIds }
 */
export const updatePlaylist = async (playlistId, payload) => {
  try {
    const response = await axios.put(`${CMS_API_BASE_URL}/playlists/${playlistId}`, payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update playlist'));
  }
};

/**
 * DELETE /sumadhura-cms/playlists/:playlistId
 */
export const deletePlaylist = async (projectId, playlistId) => {
  try {
    await axios.delete(`${CMS_API_BASE_URL}/playlists/${playlistId}`);
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete playlist'));
  }
};

/**
 * POST /sumadhura-cms/schedules/from-playlist
 * Body: { projectId, screenId, playlistId, startTime, endTime, date? } single day,
 * or { ..., startDate, endDate } for a date range (same idea as POST /schedules).
 */
export const scheduleFromPlaylist = async (payload) => {
  try {
    const response = await axios.post(`${CMS_API_BASE_URL}/schedules/from-playlist`, payload);
    notifyFeed({
      screenId: payload?.screenId,
      projectId: payload?.projectId,
      message: payload?.screenId
        ? `A package was scheduled on ${payload.screenId}.`
        : 'A package was scheduled.',
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to schedule from playlist'));
  }
};

/* ============================================================================
 * Media Organization v1.1 — library views, archive/trash/purge, folders, bulk
 *
 * All endpoints snake_case on the wire (Sumadhura convention). Contract pinned
 * at ~/master-sumadhura-cms/docs/change-sets/2026-04-22-media-organization.md.
 * Additive — none of these change existing shapes.
 * ========================================================================= */

/**
 * GET /sumadhura-cms/:projectId/:section/library?view=active|archived|trash&folder_id=<id|root>
 * Paginated list filtered by state + folder. Returns { items, folders, total }.
 * @param {{ projectId: string, section: string, view?: 'active'|'archived'|'trash', folderId?: string|null }} input
 */
export const listLibrary = async ({ projectId, section, view = 'active', folderId = null }) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    if (!section) throw new Error('section is required');
    const params = { view };
    if (folderId !== null && folderId !== undefined) {
      params.folder_id = folderId === 'root' ? 'root' : String(folderId);
    }
    const response = await axios.get(
      `${CMS_API_BASE_URL}/${encodeURIComponent(projectId)}/${encodeURIComponent(section)}/library`,
      { params },
    );
    const data = response.data || {};
    return {
      items: Array.isArray(data.items) ? data.items : [],
      folders: Array.isArray(data.folders) ? data.folders : [],
      total: Number.isFinite(data.total) ? data.total : (Array.isArray(data.items) ? data.items.length : 0),
    };
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load library view'));
  }
};

/**
 * GET /sumadhura-cms/media/:contentId/references
 * Returns { schedules:[{_id,screen_id,start_time,end_time,role}], playlists:[{_id,name,role}], total }
 */
export const getReferences = async (projectId, contentId) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    const id = String(contentId || '').trim();
    if (!id) throw new Error('contentId is required');
    const response = await axios.get(`${CMS_API_BASE_URL}/media/${encodeURIComponent(id)}/references`, {
      params: { projectId },
    });
    const data = response.data || {};
    return {
      schedules: Array.isArray(data.schedules) ? data.schedules : [],
      playlists: Array.isArray(data.playlists) ? data.playlists : [],
      total: Number.isFinite(data.total)
        ? data.total
        : (Array.isArray(data.schedules) ? data.schedules.length : 0) +
          (Array.isArray(data.playlists) ? data.playlists.length : 0),
    };
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load media references'));
  }
};

/** POST /media/:contentId/archive → { archived: true, cascaded: {...} } */
export const archiveMedia = async (projectId, contentId) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    const id = String(contentId || '').trim();
    if (!id) throw new Error('contentId is required');
    const response = await axios.post(`${CMS_API_BASE_URL}/media/${encodeURIComponent(id)}/archive`, { projectId });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to archive media'));
  }
};

/** POST /media/:contentId/unarchive → { archived: false } */
export const unarchiveMedia = async (projectId, contentId) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    const id = String(contentId || '').trim();
    if (!id) throw new Error('contentId is required');
    const response = await axios.post(`${CMS_API_BASE_URL}/media/${encodeURIComponent(id)}/unarchive`, { projectId });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to unarchive media'));
  }
};

/** POST /media/:contentId/trash → { deleted: true, cascaded: {...} } */
export const trashMedia = async (projectId, contentId) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    const id = String(contentId || '').trim();
    if (!id) throw new Error('contentId is required');
    const response = await axios.post(`${CMS_API_BASE_URL}/media/${encodeURIComponent(id)}/trash`, { projectId });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to move media to trash'));
  }
};

/** POST /media/:contentId/restore → { deleted: false } */
export const restoreMedia = async (projectId, contentId) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    const id = String(contentId || '').trim();
    if (!id) throw new Error('contentId is required');
    const response = await axios.post(`${CMS_API_BASE_URL}/media/${encodeURIComponent(id)}/restore`, { projectId });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to restore media'));
  }
};

/** DELETE /media/:contentId/purge → 204 (hard delete; Trash only). */
export const purgeMedia = async (projectId, contentId) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    const id = String(contentId || '').trim();
    if (!id) throw new Error('contentId is required');
    const response = await axios.delete(`${CMS_API_BASE_URL}/media/${encodeURIComponent(id)}/purge`, {
      params: { projectId },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to permanently delete media'));
  }
};

/** POST /media/:contentId/move  body { projectId, folder_id: string|null } */
export const moveMediaToFolder = async (projectId, contentId, folderId) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    const id = String(contentId || '').trim();
    if (!id) throw new Error('contentId is required');
    const response = await axios.post(`${CMS_API_BASE_URL}/media/${encodeURIComponent(id)}/move`, {
      projectId,
      folder_id: folderId === null || folderId === undefined ? null : String(folderId),
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to move media'));
  }
};

/**
 * POST /media/bulk
 * body { projectId, action, content_ids: [], folder_id?, category? }
 * action: 'archive'|'unarchive'|'trash'|'restore'|'purge'|'move'|'set-category'
 *
 * Fourth arg is overloaded: folderId for action='move', category for
 * action='set-category'. Pass null to clear category.
 */
export const bulkMediaAction = async (projectId, action, contentIds, extra = undefined) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    if (!action) throw new Error('action is required');
    const ids = Array.isArray(contentIds) ? contentIds.map((v) => String(v)).filter(Boolean) : [];
    if (ids.length === 0) throw new Error('content_ids is required');
    const body = { projectId, action, content_ids: ids };
    if (action === 'move') {
      body.folder_id = extra === null || extra === undefined ? null : String(extra);
    } else if (action === 'set-category') {
      body.category = extra === undefined ? null : extra;
    }
    const response = await axios.post(`${CMS_API_BASE_URL}/media/bulk`, body);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Bulk media action failed'));
  }
};

/** GET /folders/:projectId/:section → { folders: Folder[] } */
export const listFolders = async (projectId, section) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    if (!section) throw new Error('section is required');
    const response = await axios.get(
      `${CMS_API_BASE_URL}/folders/${encodeURIComponent(projectId)}/${encodeURIComponent(section)}`,
    );
    const data = response.data || {};
    return Array.isArray(data.folders) ? data.folders : [];
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to list folders'));
  }
};

/** POST /folders/:projectId/:section  body { name, parent_id?: string|null } → { folder } */
export const createFolder = async (projectId, section, { name, parentId = null } = {}) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    if (!section) throw new Error('section is required');
    const cleanName = String(name || '').trim();
    if (!cleanName) throw new Error('name is required');
    const body = { name: cleanName };
    if (parentId !== null && parentId !== undefined) body.parent_id = String(parentId);
    const response = await axios.post(
      `${CMS_API_BASE_URL}/folders/${encodeURIComponent(projectId)}/${encodeURIComponent(section)}`,
      body,
    );
    return response.data?.folder || response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create folder'));
  }
};

/** PATCH /folders/:folderId  body { name?, parent_id? } → { folder } */
export const patchFolder = async (folderId, { name, parentId } = {}) => {
  try {
    const id = String(folderId || '').trim();
    if (!id) throw new Error('folderId is required');
    const body = {};
    if (typeof name === 'string' && name.trim()) body.name = name.trim();
    if (parentId !== undefined) body.parent_id = parentId === null ? null : String(parentId);
    const response = await axios.patch(`${CMS_API_BASE_URL}/folders/${encodeURIComponent(id)}`, body);
    return response.data?.folder || response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update folder'));
  }
};

/**
 * DELETE /folders/:folderId?cascade=detach|trash
 * `detach` (default): media → root, subfolders promote to parent.
 * `trash`: recursively trashes all media in folder + subfolders.
 */
export const deleteFolder = async (folderId, cascade = 'detach') => {
  try {
    const id = String(folderId || '').trim();
    if (!id) throw new Error('folderId is required');
    const mode = cascade === 'trash' ? 'trash' : 'detach';
    const response = await axios.delete(`${CMS_API_BASE_URL}/folders/${encodeURIComponent(id)}`, {
      params: { cascade: mode },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete folder'));
  }
};
